import classNames from 'classnames'
import * as React from 'react'

import { TableAlign, TableBody, TableCell, TableRow } from '../../components/Table'
import { PriceReactionColumnDefinition, PriceReactionRowDataType, PriceReactionSubColumnDefinition } from './types'

type PriceReactionTableRowProps = {
  data: PriceReactionRowDataType
  columns: PriceReactionColumnDefinition[]
  subColumns?: Record<string, PriceReactionSubColumnDefinition[]>
  isFirst: boolean
  isLast: boolean
}

export default function PriceReactionTableRow({
  data,
  columns,
  subColumns,
  isFirst,
  isLast,
}: PriceReactionTableRowProps) {
  const { label, labelElement, labelColumnClassName, subData } = data
  const isBottomBorder = subData.length === 0 && isLast

  return (
    <TableBody>
      <TableRow isHoverable={false}>
        <TableCell
          hasLeading={false}
          align={TableAlign.Left}
          className={classNames(
            'whitespace-nowrap border-l border-t border-primary py-0.5',
            {
              'rounded-bl-md border-b': isBottomBorder,
              'rounded-tl-md': isFirst,
            },
            labelColumnClassName
          )}
        >
          {labelElement ?? <span className="overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>}
        </TableCell>

        {columns.map((column, index, array) => {
          const isLastCell = index === array.length - 1
          return (
            <TableCell
              key={column.id}
              align={column.align}
              hasLeading={false}
              className={classNames(
                'border-primary py-0.5',
                {
                  'border-t': !column.isSeparator,
                  'border-b': isBottomBorder && !column.isSeparator,
                  'border-l': column.hasLeftBorder,
                  'border-r': column.hasRightBorder || isLastCell,
                  'rounded-tl-md': isFirst && column.hasLeftSeparator,
                  'rounded-tr-md': isFirst && (column.hasRightSeparator || isLastCell),
                  'rounded-bl-md': isBottomBorder && column.hasLeftSeparator,
                  'rounded-br-md': isBottomBorder && (column.hasRightSeparator || isLastCell),
                },
                isFirst && column.topBorderClassName ? column.topBorderClassName : undefined,
                isBottomBorder && column.bottomBorderClassName ? column.bottomBorderClassName : undefined,
                column.className,
                column.getColumnClassName?.(data)
              )}
            >
              {column.renderColumn(data)}
            </TableCell>
          )
        })}
      </TableRow>

      {subData.map((item, index, array) => {
        const isSubBottomBorder = index === array.length - 1 && isLast
        return (
          <TableRow key={item.id} isHoverable={false}>
            <TableCell
              hasLeading={false}
              align={TableAlign.Left}
              className={classNames('border-l border-primary pl-1 text-muted-2', {
                'border-b': isSubBottomBorder,
                'rounded-bl-md': isSubBottomBorder,
              })}
            >
              {item.labelElement ?? <span>{item.label}</span>}
            </TableCell>
            {subColumns?.[item.id].map((subcolumn, colIdx, colArray) => {
              const column = columns[colIdx]
              const isLastCell = colIdx === colArray.length - 1
              return (
                <TableCell
                  key={subcolumn.id}
                  align={subcolumn.align}
                  hasLeading={false}
                  className={classNames(
                    subcolumn.className,
                    'border-primary py-0.5',
                    {
                      'border-b': isSubBottomBorder && !column.isSeparator,
                      'border-l': column.hasLeftBorder,
                      'border-r': isLastCell || column.hasRightBorder,
                      'rounded-bl-md': isSubBottomBorder && column.hasLeftSeparator,
                      'rounded-br-md': isSubBottomBorder && (column.hasRightSeparator || isLastCell),
                    },
                    isSubBottomBorder && column.bottomBorderClassName ? column.bottomBorderClassName : undefined,
                    column.leftBorderClassName || undefined,
                    column.rightBorderClassName || undefined
                  )}
                >
                  {subcolumn.renderColumn(item)}
                </TableCell>
              )
            })}
          </TableRow>
        )
      })}
    </TableBody>
  )
}
